import { useCallback, useEffect, useMemo, useState } from "react";
import { UserChangeEvent } from "react-onesignal";
import { useNavigation } from "@react-navigation/native";
import type { ListRenderItem } from "react-native";
import { FlatList } from "react-native";
import { createMaterialTopTabNavigator } from "@react-navigation/material-top-tabs";
import {
  actions,
  Booking,
  confirmBookingById,
  Notifications,
  openPath,
  useBookings,
  useNotifications,
  User,
  useUser,
  useUserOneSignalUpdate,
  useUserOnlineUpdate,
} from "../services";
import { LoadingScreen } from "./loading";
import { Onboard } from "./onboard";
import {
  Banner,
  BookingItemAccepted,
  BookingItemArchived,
  BookingItemVerified,
  Callout,
  CalloutRefresh,
  TopTabBadge,
} from "../components";
import { useInit } from "../services/epox/utils";

const Tab = createMaterialTopTabNavigator();

type BookingsScreenProps = {
  bookings: Booking[];
  goToPremium: () => void;
  user: User;
  onShow: (id: number) => void;
};

function VerifiedScreen({ bookings, user, ...rest }: BookingsScreenProps) {
  // Data
  const [goOnline, isUpdatingOnline] = useUserOnlineUpdate();

  // State
  const [isConfirming, setConfirming] = useState(false);

  // Vars
  const navigation = useNavigation();

  // Handlers
  const goToPremium = useCallback(() => {
    navigation.navigate("Premium");
  }, [navigation]);

  const handleAccept = useCallback(async (id: number) => {
    try {
      setConfirming(true);

      await confirmBookingById(id);
    } finally {
      setConfirming(false);
    }
  }, []);

  const handleDecline = useCallback(
    (id: number) => {
      navigation.navigate("BookingCancel", { id });
    },
    [navigation],
  );

  const handleRenderItem = useCallback<ListRenderItem<Booking>>(
    ({ item }) => (
      <BookingItemVerified
        user={user}
        booking={item}
        loading={isConfirming}
        onAccept={handleAccept}
        onDecline={handleDecline}
        {...rest}
      />
    ),
    [user, isConfirming, handleAccept, handleDecline, rest],
  );

  const handleGoOnline = useCallback(() => {
    goOnline({ online: true });
  }, [goOnline]);

  const handleBuyPremium = useCallback(() => {
    openPath(`purchase/model?id=${user.hash}`);
  }, [user.hash]);

  // Render
  if (!user.online) {
    return (
      <Callout
        icon="cloud-off-outline"
        title="You are offline"
        message={`Get bookings by going online in ${user.city?.name}`}
        action="Go online"
        onPress={handleGoOnline}
        loading={isUpdatingOnline}
        variant="offline"
      />
    );
  }

  // TODO: Display buy premium if city requires it
  const emptyEl = (
    <Callout
      icon="star-circle"
      message="Upgrade to premium to go online and receive booking requests."
      action="Buy premium"
      onPress={handleBuyPremium}
    />
  );

  const headerEl = user.offer ? (
    <Banner
      icon="star-circle"
      onPress={goToPremium}
      message={user.offer.promotion.name}
    />
  ) : null;

  return (
    <FlatList
      data={bookings}
      keyExtractor={(item) => item.id.toString()}
      ListHeaderComponent={headerEl}
      ListEmptyComponent={
        <Callout
          icon="cloud-outline"
          title="You are online"
          message="Booking requests will appear here, please make sure you can respond to them within 10 minutes."
          variant="online"
        />
      }
      renderItem={handleRenderItem}
    />
  );
}

function AcceptedScreen({ bookings, ...rest }: BookingsScreenProps) {
  // Handlers
  const handleRenderItem = useCallback<ListRenderItem<Booking>>(
    ({ item }) => {
      return <BookingItemAccepted booking={item} {...rest} />;
    },
    [rest],
  );

  // Render
  const emptyEl = (
    <Callout
      icon="calendar-outline"
      title="No upcoming bookings"
      message="Once accepted, upcoming bookings will appear here."
    />
  );

  return (
    <FlatList
      data={bookings}
      keyExtractor={(item) => item.id.toString()}
      ListEmptyComponent={emptyEl}
      renderItem={handleRenderItem}
    />
  );
}

function ArchivedScreen({ bookings, ...rest }: BookingsScreenProps) {
  // Handlers
  const handleRenderItem = useCallback<ListRenderItem<Booking>>(
    ({ item }) => {
      return <BookingItemArchived booking={item} {...rest} />;
    },
    [rest],
  );

  // Render
  const emptyEl = <Callout icon="history" message="No past bookings." />;

  return (
    <FlatList
      data={bookings}
      keyExtractor={(item) => item.id.toString()}
      ListEmptyComponent={emptyEl}
      renderItem={handleRenderItem}
    />
  );
}

export function HomeScreen() {
  // Data
  const {
    data: user,
    isLoading: isUserLoading,
    isFetching: isUserFetching,
    refetch: refetchUser,
  } = useUser();
  const {
    data: bookings,
    isLoading: isBookingsLoading,
    refetch: refetchBookings,
  } = useBookings();
  const [updateUserOneSignal] = useUserOneSignalUpdate();

  // State
  const [notificationsError, setNotificationsError] = useState<string | null>(
    null,
  );
  const [isNotificationsReady, setNotificationsReady] = useState(false);

  // Vars
  const navigation = useNavigation();
  const [
    pushPermission,
    requestPushPermission,
    isRequestingPushPermission,
    setPushPermission,
  ] = useNotifications();

  const verifiedBookings = useMemo(() => {
    return (bookings ?? []).filter(
      ({ accepted, verified }) => verified && !accepted,
    );
  }, [bookings]);

  const acceptedBookings = useMemo(() => {
    return (bookings ?? []).filter(
      ({ accepted, archived }) => accepted && !archived,
    );
  }, [bookings]);

  const archivedBookings = useMemo(() => {
    return (bookings ?? []).filter(({ archived }) => archived);
  }, [bookings]);

  const archivedActiveBookings = useMemo(() => {
    return archivedBookings.filter(
      ({ completed, review }) => completed && !review,
    );
  }, [archivedBookings]);

  // Methods
  const refresh = useCallback(() => {
    refetchUser();
    refetchBookings();
  }, [refetchUser, refetchBookings]);

  const goToPremium = useCallback(() => {
    navigation.navigate("Premium");
  }, [navigation]);

  // Handlers
  const handleShowBooking = useCallback(
    (id: number) => {
      navigation.navigate("BookingShow", { id });
    },
    [navigation],
  );

  const handleChangeOneSignalUser = useCallback(
    async ({ current: ev }: UserChangeEvent) => {
      if (ev.onesignalId) {
        await updateUserOneSignal({ uuid: ev.onesignalId });
      }
    },
    [updateUserOneSignal],
  );

  // Effects
  useInit(() => {
    async function main() {
      try {
        const oneSignalUser = await Notifications.init({
          onChangeUser: handleChangeOneSignalUser,
        });

        if (oneSignalUser.pushPermission !== null) {
          setPushPermission(oneSignalUser.pushPermission);
        }

        if (oneSignalUser.onesignalId !== null) {
          await updateUserOneSignal({ uuid: oneSignalUser.onesignalId });
        }
      } catch (err) {
        console.error("Failed to initialize OneSignal:", err);

        setNotificationsError(
          (err as Error)?.message ?? err?.toString() ?? null,
        );
      } finally {
        setNotificationsReady(true);
      }
    }

    main();
  });

  useEffect(() => {
    async function main(user: User) {
      actions.setUserId(user.id);

      try {
        Notifications.login(user.hash);
      } catch (err) {
        console.error("Failed to login to OneSignal:", err);
      }
    }

    if (isNotificationsReady && user) {
      main(user);
    }
  }, [isNotificationsReady, user]);

  useEffect(() => {
    navigation.setOptions({
      tabBarBadge: verifiedBookings.length || undefined,
    });
  }, [navigation, verifiedBookings.length]);

  // Render
  if (
    (isUserLoading && !user) ||
    (isBookingsLoading && !bookings) ||
    !isNotificationsReady
  ) {
    return <LoadingScreen />;
  }

  if (!user || !bookings) {
    return <CalloutRefresh onRefresh={refresh} />;
  }

  if (!pushPermission) {
    return (
      <Callout
        disabled={!!notificationsError}
        icon="bell"
        action="Allow"
        title={
          notificationsError
            ? "Push notifications (Error)"
            : "Push notifications"
        }
        message={
          notificationsError
            ? (notificationsError ??
              "Web notifications are not supported on this device.")
            : "You need to allow push notifications (new bookings & chats) before you can go online."
        }
        onPress={requestPushPermission}
        loading={isRequestingPushPermission}
      />
    );
  }

  if (!user.published) {
    return <Onboard loading={isUserFetching} user={user} />;
  }

  return (
    <Tab.Navigator>
      <Tab.Screen name="Requests">
        {() => (
          <VerifiedScreen
            bookings={verifiedBookings}
            goToPremium={goToPremium}
            onShow={handleShowBooking}
            user={user}
          />
        )}
      </Tab.Screen>
      <Tab.Screen
        name="Upcoming"
        options={{
          tabBarBadge:
            acceptedBookings.length > 0
              ? () => <TopTabBadge value={acceptedBookings.length} />
              : undefined,
        }}
      >
        {() => (
          <AcceptedScreen
            bookings={acceptedBookings}
            goToPremium={goToPremium}
            onShow={handleShowBooking}
            user={user}
          />
        )}
      </Tab.Screen>
      <Tab.Screen
        name="History"
        options={{
          tabBarBadge:
            archivedActiveBookings.length > 0
              ? () => <TopTabBadge value={archivedActiveBookings.length} />
              : undefined,
        }}
      >
        {() => (
          <ArchivedScreen
            bookings={archivedBookings}
            goToPremium={goToPremium}
            onShow={handleShowBooking}
            user={user}
          />
        )}
      </Tab.Screen>
    </Tab.Navigator>
  );
}
